import React, {useContext} from 'react';

import moment from 'moment';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {Calendar} from '@pb/kit-component-calendar';
import ComboBox, {ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import {CustomGroupItem, Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import {LocaleContext} from '@pb/kit-context-locale';

import {RANGE_PRESETS, getPresetRange} from '../user-history-chart/chart-period';

import {InitiatorSuggest} from './initiator-suggest';

import type {InitiatorValue} from './initiator-suggest';
import type {AdvancedFilterKey, AdvancedFilters} from './use-provisioning-history';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {ProductHistorySeries} from 'src/openapi/admin/models/ProductHistorySeries';

export const FILTER_LABELS: Record<AdvancedFilterKey, string> = {
    reason: 'Reason', productKey: 'Product', status: 'Status', initiator: 'Initiator',
    startDate: 'Starts', endDate: 'Ends',
};
const STATUSES = [{code: 'ENABLED', description: 'Enabled'}, {code: 'DISABLED', description: 'Disabled'}];
const MAX_REASON_PILL_LENGTH = 40;

export const toCalendarDate = (value?: string) => value ? new Date(`${value}T00:00:00`) : undefined;
export const toDateFilter = (value?: Date) => value
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';

export const getFilterCaption = (
    key: AdvancedFilterKey, value: string | InitiatorValue, products: ProductHistorySeries[],
) => {
    if (typeof value !== 'string') {
        return value.kind === 'user' ? value.email : value.system;
    }
    if (key === 'reason') {
        return value.slice(0, MAX_REASON_PILL_LENGTH);
    }
    const dictionaries: Partial<Record<AdvancedFilterKey, DictionaryItem[]>> = {
        productKey: products.map(product => ({code: product.productKey, description: product.productName})),
        status: STATUSES,
    };
    const dictionary = dictionaries[key] || [];
    return dictionary.find(item => item.code === value)?.description || value;
};

type Props = {
    filters: AdvancedFilters,
    onChange: React.Dispatch<React.SetStateAction<AdvancedFilters>>,
    products: ProductHistorySeries[],
};

export const ProvisioningFilters = ({filters, onChange, products}: Props) => {
    const {locale} = useContext(LocaleContext);
    const todayIso = new Date().toISOString().slice(0, 10);
    const today = toCalendarDate(todayIso)!;
    const period = filters.period ?? (filters.startDate || filters.endDate ? 'Custom' : '');
    const startDate = toCalendarDate(filters.startDate);
    const endDate = toCalendarDate(filters.endDate);
    const earliestStart = endDate ? moment(endDate).subtract(3, 'years') : undefined;
    if (earliestStart && earliestStart.clone().add(3, 'years').isBefore(endDate)) {
        earliestStart.add(1, 'day');
    }
    const latestEnd = startDate ? moment.min(moment(startDate).add(3, 'years'), moment(today)).toDate() : today;
    const update = <K extends keyof AdvancedFilters>(key: K, value: AdvancedFilters[K]) => onChange(prev => {
        const next = {...prev, [key]: value};
        if (!value) {
            delete next[key];
        }
        if (key === 'startDate' || key === 'endDate') {
            next.period = 'Custom';
        }
        return next;
    });
    const setPeriod = (value: NonNullable<AdvancedFilters['period']> | '') => onChange(prev => {
        if (value === 'Custom') {
            return {...prev, period: value};
        }
        const {startDate: oldStart, endDate: oldEnd, period: oldPeriod, ...otherFilters} = prev;
        if (!value) {
            return otherFilters;
        }
        const {startDate: nextStart, endDate: nextEnd} = getPresetRange(value, todayIso);
        return {...otherFilters, period: value, startDate: nextStart, endDate: nextEnd};
    });

    return (
        <Group direction="v" gap="s">
            <Group direction="v" gap="xxs">
                <Label htmlFor="provisioning-reason">Reason</Label>
                <Input
                    id="provisioning-reason"
                    value={filters.reason || ''}
                    placeholder="Search by reason"
                    aria-describedby="provisioning-reason-hint"
                    onInput={event => update('reason', event.currentTarget.value)}
                />
            </Group>
            {([
                ['productKey', products.map(product => ({code: product.productKey, description: product.productName}))], ['status', STATUSES],
            ] as const).map(([key, options]) => (
                <Group key={key} direction="v" gap="xxs">
                    <Label>{FILTER_LABELS[key]}</Label>
                    <ComboBox value={filters[key] || ''} onChange={(value: string) => update(key, value)}>
                        <DefaultItem value="" caption="All"/>
                        {options.map(({code, description}) => (
                            <ComboboxItem key={code} value={code} caption={description}/>
                        ))}
                    </ComboBox>
                </Group>
            ))}
            <Group direction="v" gap="xxs">
                <Label htmlFor="provisioning-initiator">Initiator</Label>
                <InitiatorSuggest
                    value={filters.initiator}
                    onChange={value => update('initiator', value)}
                />
            </Group>
            <Group direction="v" gap="m">
                <Group direction="v" gap="s">
                    <Label htmlFor="provisioning-period">Period</Label>
                    <ComboBox id="provisioning-period" value={period} onChange={setPeriod}>
                        <DefaultItem value="" caption="All time"/>
                        {RANGE_PRESETS.map(preset => (
                            <ComboboxItem
                                key={preset}
                                value={preset}
                                caption={preset === 'Last 3 months' ? 'Last 3 month' : preset}
                            />
                        ))}
                        <ComboboxItem value="Custom" caption="Custom"/>
                    </ComboBox>
                </Group>
                {period === 'Custom' ? (
                    <Group direction="h" gap="s">
                        {(['startDate', 'endDate'] as const).map(key => (
                            <CustomGroupItem key={key} label={FILTER_LABELS[key]}>
                                <div style={{width: 140}}>
                                    <Calendar
                                        id={`provisioning-${key}`}
                                        selected={toCalendarDate(filters[key])}
                                        dateFormat={mapDateFormat(locale.dateFormat, 'CALENDAR')}
                                        startDate={startDate}
                                        endDate={endDate}
                                        selectsStart={key === 'startDate' || undefined}
                                        selectsEnd={key === 'endDate' || undefined}
                                        minDate={key === 'endDate' ? startDate : earliestStart?.toDate()}
                                        maxDate={key === 'startDate' ? endDate || today : latestEnd}
                                        withRangeHint={false}
                                        isClearable={true}
                                        onChange={date => update(key, toDateFilter(date))}
                                    />
                                </div>
                            </CustomGroupItem>
                        ))}
                    </Group>
                ) : null}
            </Group>
        </Group>
    );
};
